import { z } from 'zod';

/**
 * Allowed lucide icon names that blocks can reference. Only names registered
 * in the plugin's `web/register.ts` are valid — the chat agent and the
 * editor are both constrained to this enum so pages never reference an
 * icon that fails to render.
 */
export const MARKETING_ICON_NAMES = [
  'ArrowRight',
  'Bell',
  'Box',
  'Compass',
  'ExternalLink',
  'Ghost',
  'GitPullRequest',
  'Inbox',
  'KeyRound',
  'LayoutDashboard',
  'Layers',
  'LockKeyhole',
  'Megaphone',
  'MessageCircle',
  'Play',
  'Shield',
  'ShieldCheck',
  'Sparkles',
  'TerminalSquare',
  'Users',
  'Workflow',
  'Zap',
] as const;

export const MarketingIconNameSchema = z.enum(MARKETING_ICON_NAMES);
export type MarketingIconName = z.infer<typeof MarketingIconNameSchema>;

/**
 * Named accent colors shared across marketing blocks.
 *  - `brand`   — the actual Reflex brand greens (matches `ReflexMark`).
 *  - `primary` — follows the app's theme primary CSS variable.
 *  - The remaining four are fixed Tailwind palette colors used to give
 *                individual pages their own editorial flavor.
 *
 * Keep this list in sync with `web/marketing-tokens.ts` → `MARKETING_ACCENTS`.
 */
export const MarketingAccentSchema = z.enum([
  'brand',
  'primary',
  'violet',
  'emerald',
  'amber',
  'rose',
  'neutral',
]);
export type MarketingAccentName = z.infer<typeof MarketingAccentSchema>;

/** Vertical-rhythm knob shared by blocks that support density tuning. */
export const MarketingDensitySchema = z.enum(['compact', 'default', 'loose']);
export type MarketingDensityName = z.infer<typeof MarketingDensitySchema>;

/**
 * Decorative SVG backdrops available for hero / showcase surfaces.
 *  - `glow`  — soft accent-colored radial halo.
 *  - `grid`  — blueprint-paper line grid tinted by the accent.
 *  - `noise` — fine SVG turbulence for a tactile printed feel.
 *  - `mark`  — large translucent Reflex brand mark watermark; pair with
 *              `accent: 'brand'` for the canonical front-door look.
 */
export const MarketingImagerySchema = z.enum(['none', 'glow', 'grid', 'noise', 'mark']);
export type MarketingImageryName = z.infer<typeof MarketingImagerySchema>;

/**
 * Buttons/links rendered inside hero or CTA blocks. `to` can be an
 * absolute URL or a relative path; the renderer picks between an
 * `<a>` and a react-router `<Link>` based on scheme.
 */
export const CtaLinkSchema = z.object({
  label: z.string().min(1),
  to: z.string().min(1),
  variant: z.enum(['primary', 'secondary']).default('primary'),
});
export type CtaLink = z.infer<typeof CtaLinkSchema>;

/**
 * Reference to an image stored in Runloop object storage. Image bytes
 * live behind the public proxy at `/api/public/marketing-images/:id`,
 * so the only stable identifier the block schema needs is the
 * `storageObjectId`. Width/height are optional but encouraged — the
 * editor reads intrinsic dimensions client-side and stamps them in to
 * avoid layout shift on the public page.
 *
 * `storageObjectId` is allowed to be the empty string so that
 * not-yet-uploaded image blocks remain valid in `draftBlocks`. The
 * renderer treats empty as "no image yet" and shows a placeholder
 * (or hides the image entirely on the public page). Publish-time
 * validation happens at the call site — see `marketing-page.service`.
 */
export const MarketingImageRefSchema = z.object({
  storageObjectId: z.string(),
  alt: z.string().default(''),
  caption: z.string().optional(),
  width: z.number().int().positive().optional(),
  height: z.number().int().positive().optional(),
});
export type MarketingImageRef = z.infer<typeof MarketingImageRefSchema>;

// --- Individual block schemas (discriminated by `type`) ---

const BlockIdSchema = z.string().min(1);

export const HeroBlockSchema = z.object({
  id: BlockIdSchema,
  type: z.literal('hero'),
  props: z.object({
    eyebrow: z.string().default(''),
    title: z.string().min(1),
    description: z.string().default(''),
    actions: z.array(CtaLinkSchema).default([]),
    /**
     * Visual variant. `default` = current look, `compact` = slimmer for
     * secondary pages, `showcase` = big type + heavy gradient for tent-pole
     * pages. Omit to render as `default` — the renderer applies the fallback
     * so existing pages and seeds continue to work without migration.
     */
    variant: z.enum(['compact', 'default', 'showcase']).optional(),
    /** Per-page accent tint applied to eyebrow, gradient, and imagery. */
    accent: MarketingAccentSchema.optional(),
    /** Decorative background layer. */
    imagery: MarketingImagerySchema.optional(),
    /**
     * Optional editorial image rendered alongside the headline copy.
     * Adds visual weight without replacing the decorative `imagery`
     * layer (which lives behind the gradient). When omitted, hero
     * blocks render exactly as before — Phase 2 is purely additive.
     */
    image: MarketingImageRefSchema.optional(),
    /**
     * How the optional `image` is composed with the headline copy:
     * - `side` (default) = two-column layout with image to the right
     * - `background` = image fills the hero behind a darkening
     *    gradient overlay; `imagery` is suppressed in this mode
     * - `below` = image stacked beneath the title/description block
     *
     * Ignored when `image` is absent.
     */
    imageLayout: z.enum(['side', 'background', 'below']).optional(),
  }),
});
export type HeroBlock = z.infer<typeof HeroBlockSchema>;

export const SectionHeaderBlockSchema = z.object({
  id: BlockIdSchema,
  type: z.literal('section-header'),
  props: z.object({
    sectionLabel: z.string().default(''),
    title: z.string().min(1),
    description: z.string().default(''),
    /** Accent applied to the section label. Defaults to `neutral` at render time. */
    accent: MarketingAccentSchema.optional(),
    /** Vertical rhythm above the header. Defaults to `default` at render time. */
    density: MarketingDensitySchema.optional(),
  }),
});
export type SectionHeaderBlock = z.infer<typeof SectionHeaderBlockSchema>;

/**
 * Plain prose block: an optional title plus body copy, rendered in the
 * standard muted, width-capped description style. Unlike `section-header`
 * it carries no eyebrow/label slot — an empty `title` leaves no gap on
 * the public page. Body text splits on blank lines into paragraphs.
 */
export const ProseBlockSchema = z.object({
  id: BlockIdSchema,
  type: z.literal('prose'),
  props: z.object({
    title: z.string().default(''),
    body: z.string().min(1),
  }),
});
export type ProseBlock = z.infer<typeof ProseBlockSchema>;

/**
 * Allowed column counts for `feature-grid`. Encoded as a plain integer
 * with a `refine()` rather than a `z.union([z.literal(...), ...])` so
 * the JSON schema we ship to Gemini's function-declaration validator
 * does not contain a non-string enum (which fails with `(TYPE_STRING)`
 * and aborts the entire chat request — same workaround as
 * `routes.chat.ts`'s `replace_data` in plugin-replay).
 */
const FEATURE_GRID_COLUMNS = [2, 3, 4] as const;

export const FeatureGridBlockSchema = z.object({
  id: BlockIdSchema,
  type: z.literal('feature-grid'),
  props: z.object({
    columns: z
      .number()
      .int()
      .refine((n): n is (typeof FEATURE_GRID_COLUMNS)[number] =>
        (FEATURE_GRID_COLUMNS as readonly number[]).includes(n),
      )
      .default(3),
    /**
     * `cards` = current `<Card>`-backed tiles, `bare` = no card chrome
     * (dense), `numbered` = prepends step indices instead of icons.
     * Omit to render as `cards`.
     */
    variant: z.enum(['cards', 'bare', 'numbered']).optional(),
    /** Accent used to tint every feature's icon chip. Defaults to `primary`. */
    accent: MarketingAccentSchema.optional(),
    /** Grid gap + internal padding. Defaults to `default`. */
    density: MarketingDensitySchema.optional(),
    features: z
      .array(
        z.object({
          icon: MarketingIconNameSchema,
          title: z.string().min(1),
          description: z.string().min(1),
          /**
           * Optional per-feature image. When set, the renderer shows
           * the image in place of the icon chip — useful for partner
           * logos, screenshots, or product shots.
           */
          image: MarketingImageRefSchema.optional(),
          /**
           * Optional link destination. When set, the feature card becomes
           * a navigable link. Use for feature cards that should navigate
           * to detail pages (e.g. concept pages from a quickstart).
           */
          to: z.string().min(1).optional(),
        }),
      )
      .min(1),
  }),
});
export type FeatureGridBlock = z.infer<typeof FeatureGridBlockSchema>;

/** See note on `FEATURE_GRID_COLUMNS` for why this is not a union of literals. */
const EXAMPLE_GRID_COLUMNS = [1, 2, 3] as const;

export const ExampleGridBlockSchema = z.object({
  id: BlockIdSchema,
  type: z.literal('example-grid'),
  props: z.object({
    columns: z
      .number()
      .int()
      .refine((n): n is (typeof EXAMPLE_GRID_COLUMNS)[number] =>
        (EXAMPLE_GRID_COLUMNS as readonly number[]).includes(n),
      )
      .default(3),
    /**
     * `cards` = current `<Card>`-backed tiles, `timeline` = connects the
     * ordered steps with a vertical spine for narrative flows.
     * Omit to render as `cards`.
     */
    variant: z.enum(['cards', 'timeline']).optional(),
    /** Accent applied to icon chips and step markers. Defaults to `primary`. */
    accent: MarketingAccentSchema.optional(),
    examples: z
      .array(
        z.object({
          badge: z.string().default(''),
          icon: MarketingIconNameSchema,
          title: z.string().min(1),
          tagline: z.string().default(''),
          steps: z.array(z.string().min(1)).min(1),
          /**
           * Optional image shown above the example title. Replaces
           * the icon when present; useful when each example needs a
           * unique screenshot.
           */
          image: MarketingImageRefSchema.optional(),
          /**
           * Optional link for the example. When provided with `cta`, renders
           * a button linking to this path.
           */
          to: z.string().optional(),
          /**
           * CTA button label. Only rendered when `to` is also provided.
           */
          cta: z.string().optional(),
        }),
      )
      .min(1),
  }),
});
export type ExampleGridBlock = z.infer<typeof ExampleGridBlockSchema>;

export const CtaBannerBlockSchema = z.object({
  id: BlockIdSchema,
  type: z.literal('cta-banner'),
  props: z.object({
    /**
     * Surface variant. `card`/`muted` = existing treatments (default).
     * `gradient` = soft accent-tinted gradient. `showcase` = full-bleed
     * dramatic gradient with optional imagery layer.
     */
    variant: z.enum(['card', 'muted', 'gradient', 'showcase']).default('card'),
    /** Accent tint for gradient / showcase variants. Defaults to `primary`. */
    accent: MarketingAccentSchema.optional(),
    title: z.string().min(1),
    description: z.string().default(''),
    primary: CtaLinkSchema,
    secondary: CtaLinkSchema.optional(),
    /**
     * Optional accent image shown to the right of the banner copy.
     * Only rendered for `gradient` and `showcase` variants — `card`
     * and `muted` keep their compact layout regardless.
     */
    image: MarketingImageRefSchema.optional(),
  }),
});
export type CtaBannerBlock = z.infer<typeof CtaBannerBlockSchema>;

export const RichTextBlockSchema = z.object({
  id: BlockIdSchema,
  type: z.literal('rich-text'),
  props: z.object({
    markdown: z.string().default(''),
  }),
});
export type RichTextBlock = z.infer<typeof RichTextBlockSchema>;

/**
 * Standalone image block. Renders one image with optional caption and
 * a small set of layout knobs (max-width, alignment, aspect ratio).
 * Use `feature-grid` or `example-grid` for image-as-icon contexts —
 * this block is for editorial imagery (screenshots, illustrations).
 */
export const ImageBlockSchema = z.object({
  id: BlockIdSchema,
  type: z.literal('image'),
  props: z.object({
    image: MarketingImageRefSchema,
    /**
     * Maximum width the image is allowed to occupy inside the page's
     * content column. `default` matches the surrounding text width;
     * `wide` pokes out into the gutter; `full` is edge-to-edge for
     * dramatic screenshots; `narrow` keeps it text-width on large
     * displays.
     */
    maxWidth: z.enum(['narrow', 'default', 'wide', 'full']).default('default'),
    align: z.enum(['left', 'center', 'right']).default('center'),
    /**
     * Forces an aspect ratio on the image. `auto` lets the intrinsic
     * dimensions stamped on the `MarketingImageRef` win; the fixed
     * ratios center-crop via `object-cover` so heroes stay neat even
     * if a contributor uploads a portrait screenshot.
     */
    aspect: z.enum(['auto', '16:9', '4:3', '1:1']).default('auto'),
  }),
});
export type ImageBlock = z.infer<typeof ImageBlockSchema>;

export const IndexGridBlockSchema = z.object({
  id: BlockIdSchema,
  type: z.literal('index-grid'),
  props: z.object({
    excludeCurrent: z.boolean().default(true),
  }),
});
export type IndexGridBlock = z.infer<typeof IndexGridBlockSchema>;

/**
 * Playback speed multipliers exposed by the replay engine. Kept in sync
 * with `REPLAY_SPEEDS` in `@reflex/plugin-replay` — duplicated here
 * rather than imported so the marketing schema stays dependency-free on
 * the server side (shared/ is pure Zod + types).
 */
export const REPLAY_SPEED_VALUES = [1, 2, 4, 8, 16] as const;
export type ReplayBlockSpeed = (typeof REPLAY_SPEED_VALUES)[number];

/**
 * Multi-image block for galleries and side-by-side editorial layouts.
 *
 * Variants:
 *   - `single`        — one image (collapses to behave like `image` block,
 *                       kept so authors who pick "media" by accident don't
 *                       lose their layout knob).
 *   - `gallery-2`     — two-up responsive grid (good for before/after).
 *   - `gallery-3`     — three-up responsive grid for product shots.
 *   - `side-by-side`  — leading image + headline/body copy on the other
 *                       side; flips at the `sm` breakpoint.
 *
 * `images` is bounded at 1–6 entries; renderers slice based on variant
 * (gallery-2 takes the first two, gallery-3 the first three, etc.) so
 * over-supplied entries are silently ignored rather than failing
 * validation when authors switch variants mid-edit.
 */
export const MediaBlockSchema = z.object({
  id: BlockIdSchema,
  type: z.literal('media'),
  props: z.object({
    variant: z.enum(['single', 'gallery-2', 'gallery-3', 'side-by-side']).default('single'),
    images: z.array(MarketingImageRefSchema).min(1).max(6),
    /** Optional headline rendered above the media (or beside it for side-by-side). */
    title: z.string().optional(),
    /** Optional markdown body — only consumed by the `side-by-side` variant. */
    body: z.string().optional(),
    /** Accent applied to chrome (caption underlines, side-by-side eyebrow). */
    accent: MarketingAccentSchema.optional(),
  }),
});
export type MediaBlock = z.infer<typeof MediaBlockSchema>;

/**
 * Shared chrome / playback knobs every replay-block variant accepts.
 * Kept as a separate object so the agent / flow source variants can
 * extend it without duplicating the field list.
 */
const ReplayBlockBaseProps = {
  autoPlay: z.boolean().default(false),
  showControls: z.boolean().default(true),
  stealth: z.boolean().default(false),
  /**
   * When true, forces the viewer to click the highlighted button at
   * every user-initiated action in the replay before playback resumes.
   * Implies `stealth` (chrome hidden, autoplay on) from the engine's
   * point of view. Useful for hands-on marketing demos. (Agent source
   * only — flow replays don't expose user-action gates.)
   */
  interactive: z.boolean().default(false),
  /**
   * Initial playback speed multiplier (1×, 2×, 4×, 8×, 16×). Encoded as
   * a plain integer with `refine()` rather than a union of literals so
   * the JSON schema sent to Gemini does not contain a non-string enum.
   * See note on `FEATURE_GRID_COLUMNS`.
   */
  speed: z
    .number()
    .int()
    .refine((n): n is ReplayBlockSpeed => (REPLAY_SPEED_VALUES as readonly number[]).includes(n))
    .default(1),
  aspect: z.enum(['16:9', '4:3', 'square', 'auto']).default('16:9'),
  caption: z.string().default(''),
};

const ReplayBlockAgentPropsSchema = z.object({
  source: z.literal('agent').default('agent'),
  replayId: z.string().min(1),
  ...ReplayBlockBaseProps,
});

const ReplayBlockFlowPropsSchema = z.object({
  source: z.literal('flow'),
  flowReplayId: z.string().min(1),
  ...ReplayBlockBaseProps,
});

/**
 * Replay block. Defaults to the `agent` source for backwards
 * compatibility — pre-`source` rows on disk (only `replayId` set) are
 * coerced into `{ source: 'agent', ... }` by the preprocess below so
 * existing marketing pages keep validating.
 */
export const ReplayBlockSchema = z.object({
  id: BlockIdSchema,
  type: z.literal('replay'),
  props: z.preprocess(
    (raw) => {
      if (raw && typeof raw === 'object' && !('source' in raw)) {
        return { source: 'agent', ...raw };
      }
      return raw;
    },
    z.discriminatedUnion('source', [ReplayBlockAgentPropsSchema, ReplayBlockFlowPropsSchema]),
  ),
});
export type ReplayBlock = z.infer<typeof ReplayBlockSchema>;
export type ReplayBlockProps = ReplayBlock['props'];
export type ReplayBlockAgentProps = z.infer<typeof ReplayBlockAgentPropsSchema>;
export type ReplayBlockFlowProps = z.infer<typeof ReplayBlockFlowPropsSchema>;

// --- Live-aware block schemas (PRD-9a, Workstream C) ---
//
// These blocks render workspace-aware data inside otherwise-public docs
// pages. Their schemas live in the shared `BlockSchema` discriminated
// union (ADR-001 — single-rooted registry), but their renderer
// implementations live in `plugin-docs/src/web/blocks/live/` and are
// injected into the docs renderer via the `registry` prop on
// {@link PageRenderer} (so marketing pages don't drag in plugin-docs's
// React Query / auth dependencies).
//
// Marketing pages that happen to declare a live-aware block render it as
// "Unsupported block type" — by design, since the marketing block picker
// does not surface these types to authors.

/**
 * Renders the org's installed plugins. Uses the existing
 * `/api/orgs/:org/plugins` endpoint (per-domain, already org-scoped).
 *
 * Anonymous readers and Storybook stories see the colocated fixture; the
 * docs MCP plaintext companion (Workstream C4) renders the fixture
 * summary so live workspace data never leaks through MCP.
 */
export const InstalledPluginsBlockSchema = z.object({
  id: BlockIdSchema,
  type: z.literal('installed-plugins'),
  props: z.object({
    title: z.string().default(''),
    description: z.string().default(''),
    /**
     * Optional plugin-name substring to narrow the rendered list. When
     * set, only plugins whose `pluginName` contains the substring (case
     * insensitive) are shown. Useful for embedding a "is the X plugin
     * installed?" prompt into a per-plugin reference page.
     */
    filter: z.string().optional(),
    /** Optional cap on rows shown. Omit for no cap. */
    limit: z.number().int().positive().optional(),
  }),
});
export type InstalledPluginsBlock = z.infer<typeof InstalledPluginsBlockSchema>;

/**
 * Renders the org's configured integrations — plugins whose category
 * implies an external service connection (Slack, GitHub, Sentry, Linear,
 * etc.). Filter by plugin name to show a single integration's status.
 *
 * Reuses the same `/api/orgs/:org/plugins` endpoint as
 * `<InstalledPlugins>` — the differentiation is presentation-only (this
 * block emphasises connection status rather than the full plugin list).
 */
export const ConfiguredIntegrationsBlockSchema = z.object({
  id: BlockIdSchema,
  type: z.literal('configured-integrations'),
  props: z.object({
    title: z.string().default(''),
    description: z.string().default(''),
    /**
     * Optional plugin-name filter. When set, only the matching plugin
     * is rendered (e.g. `filter: 'slack'` → "Slack is configured").
     */
    filter: z.string().optional(),
  }),
});
export type ConfiguredIntegrationsBlock = z.infer<typeof ConfiguredIntegrationsBlockSchema>;

/**
 * Renders the org's configured MCP servers (custom + Reflex Self).
 * Reads from the existing `/api/mcp-configs` endpoint (per-domain,
 * already org-scoped).
 */
export const McpEndpointsBlockSchema = z.object({
  id: BlockIdSchema,
  type: z.literal('mcp-endpoints'),
  props: z.object({
    title: z.string().default(''),
    description: z.string().default(''),
    /**
     * `enabled-only` (default) hides disabled rows; `all` shows them
     * with a status badge so admins can see what's been set up but not
     * activated.
     */
    visibility: z.enum(['enabled-only', 'all']).default('enabled-only'),
  }),
});
export type McpEndpointsBlock = z.infer<typeof McpEndpointsBlockSchema>;

/**
 * Renders the org's available agent personas. Reads from the existing
 * `/api/personas` endpoint.
 */
export const AvailablePersonasBlockSchema = z.object({
  id: BlockIdSchema,
  type: z.literal('available-personas'),
  props: z.object({
    title: z.string().default(''),
    description: z.string().default(''),
    /** Optional cap on rows shown. Omit for no cap. */
    limit: z.number().int().positive().optional(),
  }),
});
export type AvailablePersonasBlock = z.infer<typeof AvailablePersonasBlockSchema>;

const YouTubeVideoEmbedPropsSchema = z.object({
  /** Video hosting provider. */
  provider: z.literal('youtube'),
  /** YouTube video ID. */
  videoId: z.string().regex(/^[A-Za-z0-9_-]{11}$/, 'videoId must be an 11-character YouTube ID'),
  /**
   * Optional title. When set, it renders as a heading above the player
   * (and doubles as the iframe's accessible name). Omit it when the video
   * already sits under its own section header.
   */
  title: z.string().min(1).optional(),
  /** Optional description shown below the video. */
  description: z.string().optional(),
});

const DescriptVideoEmbedPropsSchema = z.object({
  /** Video hosting provider. */
  provider: z.literal('descript'),
  /** Descript share ID from share.descript.com/view/<id>. */
  videoId: z.string().regex(/^[A-Za-z0-9_-]{6,64}$/, 'videoId must be a Descript share ID'),
  /**
   * Optional title. When set, it renders as a heading above the player
   * (and doubles as the iframe's accessible name). Omit it when the video
   * already sits under its own section header.
   */
  title: z.string().min(1).optional(),
  /** Optional description shown below the video. */
  description: z.string().optional(),
});

/**
 * Embeds an external video. Renders a responsive
 * iframe with the video player. Use for product intro videos, tutorials,
 * or any external video content that should be embedded in docs/marketing.
 */
export const VideoEmbedBlockSchema = z.object({
  id: BlockIdSchema,
  type: z.literal('video-embed'),
  props: z.discriminatedUnion('provider', [
    YouTubeVideoEmbedPropsSchema,
    DescriptVideoEmbedPropsSchema,
  ]),
});
export type VideoEmbedBlock = z.infer<typeof VideoEmbedBlockSchema>;

/**
 * Renders a "Start here" row with chips linking to key docs. Uses manifest
 * data to resolve doc entries. Docs-only block (not shown in marketing editor).
 */
export const DocsStartHereBlockSchema = z.object({
  id: BlockIdSchema,
  type: z.literal('docs-start-here'),
  props: z.object({
    /**
     * Optional ordered list of slugs to show as chips. When omitted,
     * defaults to a standard set (overview, quickstart, plugins/overview,
     * developer/plugins/getting-started).
     */
    slugs: z.array(z.string().min(1)).optional(),
  }),
});
export type DocsStartHereBlock = z.infer<typeof DocsStartHereBlockSchema>;

/**
 * Renders the full docs directory: grouped cards + recently updated section.
 * Pulls docs from the manifest. Docs-only block (not shown in marketing editor).
 */
export const DocsDirectoryBlockSchema = z.object({
  id: BlockIdSchema,
  type: z.literal('docs-directory'),
  props: z.object({
    /** Whether to show the header with title/description. Defaults to true. */
    showHeader: z.boolean().optional(),
    /** Whether to show the recently updated section. Defaults to true. */
    showRecentlyUpdated: z.boolean().optional(),
  }),
});
export type DocsDirectoryBlock = z.infer<typeof DocsDirectoryBlockSchema>;

/**
 * Renders a list of in-app tutorials (from plugin-tutorial) with Start /
 * Restart actions. The `tutorials` prop lists tutorial *names*; their titles
 * and descriptions are resolved from the tutorial registry so docs stay in
 * sync with the tours themselves. Live-aware, docs-only block (not shown in
 * the marketing editor).
 */
export const TutorialsListBlockSchema = z.object({
  id: BlockIdSchema,
  type: z.literal('tutorials-list'),
  props: z.object({
    title: z.string().default(''),
    description: z.string().default(''),
    /** Ordered tutorial names to display (resolved via the tutorial registry). */
    tutorials: z.array(z.string().min(1)).min(1),
  }),
});
export type TutorialsListBlock = z.infer<typeof TutorialsListBlockSchema>;

/**
 * A single release rendered by the {@link ReleaseTimelineBlockSchema}. The
 * shape mirrors the authored `releases/<version>.json` content (overview,
 * highlights, features, case studies) merged with the parsed `CHANGELOG.md`
 * entry (`date`, `changelogSummary`). The changelog plugin bakes
 * this array into the block's props at build time so the renderer stays pure.
 */
export const ReleaseTimelineEntrySchema = z.object({
  version: z.string().min(1),
  date: z.string().optional(),
  isLatest: z.boolean().optional(),
  releaseType: z.enum(['major', 'minor', 'patch', 'hotfix']).optional(),
  /** Optional short headline rendered above the overview. Authored in `releases/<version>.json`. */
  title: z.string().optional(),
  overview: z.string().optional(),
  highlights: z.array(z.string()).optional(),
  features: z
    .array(
      z.object({
        title: z.string(),
        description: z.string().optional(),
        tutorials: z.array(z.object({ label: z.string(), href: z.string() })).optional(),
      }),
    )
    .optional(),
  caseStudies: z
    .array(
      z.object({
        title: z.string(),
        description: z.string().optional(),
        href: z.string(),
      }),
    )
    .optional(),
  changelogSummary: z.string().optional(),
});
export type ReleaseTimelineEntry = z.infer<typeof ReleaseTimelineEntrySchema>;

/**
 * Renders the releases page as a vertical timeline of versions, each with its
 * release-type badge, overview, highlights, features, case studies, and
 * changelog body. Live-aware, docs-only block (not shown in the marketing
 * editor): the changelog plugin generates a `/docs/changelog` page carrying one
 * of these blocks, fed at build time from `CHANGELOG.md` + `releases/*.json`,
 * and `plugin-docs` renders it via the live block registry.
 */
export const ReleaseTimelineBlockSchema = z.object({
  id: BlockIdSchema,
  type: z.literal('release-timeline'),
  props: z.object({
    releases: z.array(ReleaseTimelineEntrySchema),
    /** Current app version surfaced as a `v{x.y.z}` badge. */
    appVersion: z.string().optional(),
  }),
});
export type ReleaseTimelineBlock = z.infer<typeof ReleaseTimelineBlockSchema>;

/** Discriminated union of every supported block. */
export const BlockSchema = z.discriminatedUnion('type', [
  HeroBlockSchema,
  SectionHeaderBlockSchema,
  ProseBlockSchema,
  FeatureGridBlockSchema,
  ExampleGridBlockSchema,
  CtaBannerBlockSchema,
  RichTextBlockSchema,
  ImageBlockSchema,
  MediaBlockSchema,
  IndexGridBlockSchema,
  ReplayBlockSchema,
  InstalledPluginsBlockSchema,
  ConfiguredIntegrationsBlockSchema,
  McpEndpointsBlockSchema,
  AvailablePersonasBlockSchema,
  VideoEmbedBlockSchema,
  DocsStartHereBlockSchema,
  DocsDirectoryBlockSchema,
  TutorialsListBlockSchema,
  ReleaseTimelineBlockSchema,
]);
export type Block = z.infer<typeof BlockSchema>;
export type BlockType = Block['type'];

export const BlocksSchema = z.array(BlockSchema).min(1, 'A page must contain at least one block');

/** Runtime-friendly list of every block type. */
export const BLOCK_TYPES: readonly BlockType[] = [
  'hero',
  'section-header',
  'prose',
  'feature-grid',
  'example-grid',
  'cta-banner',
  'rich-text',
  'image',
  'media',
  'index-grid',
  'replay',
  'installed-plugins',
  'configured-integrations',
  'mcp-endpoints',
  'available-personas',
  'video-embed',
  'docs-start-here',
  'docs-directory',
  'tutorials-list',
  'release-timeline',
] as const;

/**
 * Subset of {@link BlockType} that is rendered by `plugin-docs`'s live-aware
 * block components. Marketing's `BLOCK_REGISTRY` does not contain entries
 * for these types — `<DocPageRenderer>` injects them via the `registry`
 * override prop on {@link PageRenderer}.
 */
export const LIVE_AWARE_BLOCK_TYPES = [
  'installed-plugins',
  'configured-integrations',
  'mcp-endpoints',
  'available-personas',
  'docs-start-here',
  'docs-directory',
  'tutorials-list',
  'release-timeline',
] as const satisfies readonly BlockType[];
export type LiveAwareBlockType = (typeof LIVE_AWARE_BLOCK_TYPES)[number];

/**
 * Block types that the marketing renderer knows how to render directly
 * (i.e. that exist in `BLOCK_REGISTRY`). The marketing chat agent and the
 * editor's block picker advertise this list to authors so live-aware
 * docs blocks (PRD-9a) — which exist in `BlockSchema` but live in
 * `plugin-docs` — never get suggested for marketing pages.
 */
export const MARKETING_BLOCK_TYPES = [
  'hero',
  'section-header',
  'prose',
  'feature-grid',
  'example-grid',
  'cta-banner',
  'rich-text',
  'image',
  'media',
  'index-grid',
  'replay',
  'video-embed',
] as const satisfies readonly BlockType[];
export type MarketingBlockType = (typeof MARKETING_BLOCK_TYPES)[number];

/**
 * Discriminated union of blocks that the marketing MCP tools and editor accept.
 * Excludes live-aware docs blocks (PRD-9a) that exist in {@link BlockSchema} but
 * are only renderable by `plugin-docs`. Use this schema for `add_marketing_block`
 * input validation to ensure MCP callers cannot insert live-aware blocks into
 * marketing pages.
 */
export const MarketingBlockSchema = z.discriminatedUnion('type', [
  HeroBlockSchema,
  SectionHeaderBlockSchema,
  ProseBlockSchema,
  FeatureGridBlockSchema,
  ExampleGridBlockSchema,
  CtaBannerBlockSchema,
  RichTextBlockSchema,
  ImageBlockSchema,
  MediaBlockSchema,
  IndexGridBlockSchema,
  ReplayBlockSchema,
  VideoEmbedBlockSchema,
]);
export type MarketingBlock = z.infer<typeof MarketingBlockSchema>;

// --- Shared JSON Schema definitions ---

// Registry ids for the sub-schemas reused across block variants.
// `z.toJSONSchema` hoists a registered schema into `definitions` and emits a
// `$ref` at each use site instead of a full copy, so any generated JSON Schema
// covering a block tree carries each shared shape once rather than per
// variant. `MarketingImageRef` alone appears at 14 use sites.
//
// Register only shapes that repeat and are larger than a `$ref`. A schema
// carrying `.default()` costs more as a ref than inline, because the default
// forces an `allOf` wrapper around it — hence `CtaLink` (whose `variant` field
// defaults) is registered as a whole object rather than field by field.
z.globalRegistry.add(MarketingIconNameSchema, { id: 'MarketingIconName' });
z.globalRegistry.add(MarketingAccentSchema, { id: 'MarketingAccent' });
z.globalRegistry.add(CtaLinkSchema, { id: 'CtaLink' });
z.globalRegistry.add(MarketingImageRefSchema, { id: 'MarketingImageRef' });

/**
 * Collects every agent `replayId` referenced by a block tree. Used by
 * `MarketingPageService.refreshReplayIndex` to populate the
 * `marketing_public_replays` analytics index when a page is published.
 * Flow-source replay blocks are skipped — flow replays are reachable
 * via `plugin-flows`' canonical share-link endpoint and don't go
 * through a marketing publish gate.
 *
 * Tolerant of legacy block shapes where `source` is absent: those rows
 * predate the discriminator and are always agent-source, matching the
 * runtime `preprocess` on {@link ReplayBlockSchema}.
 */
export function collectReplayIds(blocks: Block[]): string[] {
  const ids = new Set<string>();
  for (const block of blocks) {
    if (block.type !== 'replay') continue;
    const props = block.props as { source?: 'agent' | 'flow'; replayId?: string };
    const isAgent = props.source === undefined || props.source === 'agent';
    if (isAgent && props.replayId) ids.add(props.replayId);
  }
  return [...ids];
}

/**
 * Collects every Runloop `storageObjectId` referenced as a marketing
 * image by a block tree. Walks future image-bearing blocks too — kept
 * deliberately broad so a future orphan-cleanup sweep can intersect
 * "objects we still reference" with "objects in storage" without
 * needing schema-level changes.
 *
 * Pass `pageRefs` to fold in page-level images (e.g. `ogImage`) so the
 * orphan sweep treats them as live references too.
 */
export function collectImageIds(
  blocks: Block[],
  pageRefs?: (MarketingImageRef | undefined | null)[],
): string[] {
  const ids = new Set<string>();
  const add = (ref: MarketingImageRef | undefined | null) => {
    if (ref?.storageObjectId) ids.add(ref.storageObjectId);
  };
  for (const block of blocks) {
    if (block.type === 'image') add(block.props.image);
    else if (block.type === 'hero') add(block.props.image);
    else if (block.type === 'cta-banner') add(block.props.image);
    else if (block.type === 'feature-grid') {
      for (const f of block.props.features) add(f.image);
    } else if (block.type === 'example-grid') {
      for (const e of block.props.examples) add(e.image);
    } else if (block.type === 'media') {
      for (const img of block.props.images) add(img);
    }
  }
  if (pageRefs) {
    for (const ref of pageRefs) add(ref);
  }
  return [...ids];
}

// --- Page-level schemas ---

export const PageMetaSchema = z.object({
  slug: z
    .string()
    .min(1)
    .max(80)
    .regex(
      /^[a-z0-9]+(?:-[a-z0-9]+)*$/,
      'slug must be kebab-case (lowercase letters, digits, dashes)',
    ),
  title: z.string().min(1),
  description: z.string().default(''),
  icon: MarketingIconNameSchema.default('Sparkles'),
  order: z.number().int().default(100),
  /**
   * Open Graph / Twitter Card image used by the SSR layer when emitting
   * `og:image` and `twitter:image` for shareable link unfurls. Optional —
   * when absent, the SSR renderer falls back to the first hero/image
   * block in the published snapshot. Stored on the page (not the version)
   * so authors can update social cards without republishing the body.
   */
  ogImage: MarketingImageRefSchema.optional(),
});
export type PageMeta = z.infer<typeof PageMetaSchema>;

export const MarketingPageSchema = z.object({
  id: z.string(),
  ...PageMetaSchema.shape,
  draftBlocks: BlocksSchema,
  /**
   * Id of the currently-published version row, or `null` if the page
   * is not published. Replaces the legacy `publishedBlocks` field —
   * consumers should fetch `GET /marketing-pages/:id/versions/:vid`
   * to read the live snapshot's blocks.
   */
  publishedVersionId: z.string().nullable(),
  publishedAt: z.number().nullable(),
  /**
   * Owning organization id when multi-tenancy is enabled, or `null`
   * for legacy / globally-visible pages (seeded canonical pages,
   * pre-migration rows). Public viewing of marketing pages is
   * intentionally cross-tenant — `null` rows are visible to every
   * tenant via `rowInOrgOrShared` for admin/editor reads.
   */
  organizationId: z.string().nullable(),
  createdAt: z.number(),
  updatedAt: z.number(),
});
export type MarketingPage = z.infer<typeof MarketingPageSchema>;

/**
 * Source that triggered a version snapshot. Surfaced in the versions
 * popover as a small badge so authors can tell apart their own saves
 * from chat-driven implicit snapshots and system auto-snapshots
 * (e.g. the safe-restore guard).
 */
export const MarketingVersionSourceSchema = z.enum(['user', 'chat', 'system']);
export type MarketingVersionSource = z.infer<typeof MarketingVersionSourceSchema>;

/** A single immutable snapshot of a page's blocks. */
export const MarketingPageVersionSchema = z.object({
  id: z.string(),
  pageId: z.string(),
  versionNumber: z.number().int(),
  blocks: BlocksSchema,
  note: z.string().nullable(),
  /** User id that triggered the snapshot, if known. `null` for system/chat snapshots without a user. */
  createdByUserId: z.string().nullable(),
  /** How the snapshot was triggered. Defaults to `'user'` for legacy rows. */
  createdBySource: MarketingVersionSourceSchema,
  createdAt: z.number(),
});
export type MarketingPageVersion = z.infer<typeof MarketingPageVersionSchema>;

/**
 * List-friendly summary of a version. Omits the heavy `blocks` payload
 * so the version dropdown in the editor stays cheap to fetch.
 */
export const MarketingPageVersionSummarySchema = MarketingPageVersionSchema.omit({
  blocks: true,
});
export type MarketingPageVersionSummary = z.infer<typeof MarketingPageVersionSummarySchema>;

/** Body for `POST /marketing-pages/:id/versions` (save-current-draft). */
export const SaveVersionPayloadSchema = z.object({
  note: z.string().max(500).optional(),
});
export type SaveVersionPayload = z.infer<typeof SaveVersionPayloadSchema>;

/** Body for `POST /marketing-pages/:id/publish`. */
export const PublishPayloadSchema = z.object({
  note: z.string().max(500).optional(),
});
export type PublishPayload = z.infer<typeof PublishPayloadSchema>;

/**
 * Per-block change record emitted by `diffBlocks`. `kind` says how the
 * block has changed relative to the live snapshot:
 *  - `added`     — present in draft, not in live (block was created since publish)
 *  - `removed`   — present in live, not in draft (block was deleted since publish)
 *  - `modified`  — same id in both, but props or type differs (block was edited)
 *  - `unchanged` — identical content in both (still surfaced so the
 *                  diff sheet can render the full draft outline)
 */
export const MarketingBlockChangeSchema = z.object({
  kind: z.enum(['added', 'removed', 'modified', 'unchanged']),
  blockId: z.string(),
  blockType: z.string(),
  /**
   * Best-effort title for the block, used by the diff UI when listing
   * what changed. Falls back to the block type when no obvious label
   * exists (e.g. `index-grid`).
   */
  title: z.string().nullable(),
  /** Prop keys whose value differs (only populated when `kind === 'modified'`). */
  changedProps: z.array(z.string()),
});
export type MarketingBlockChange = z.infer<typeof MarketingBlockChangeSchema>;

/**
 * Aggregate "draft vs published" diff. Drives the editor's status pill,
 * publish dialog, and version-comparison sheet.
 */
export const MarketingDiffSummarySchema = z.object({
  /** True when the page is unpublished. */
  isUnpublished: z.boolean(),
  /**
   * True when the page is published AND the draft differs from the
   * live snapshot. The editor uses this to enable the "Discard draft"
   * affordance and to tag the status pill with a change count.
   */
  isDirty: z.boolean(),
  added: z.number().int(),
  removed: z.number().int(),
  modified: z.number().int(),
  unchanged: z.number().int(),
  /** Per-block change list, in draft order followed by removed-from-live. */
  changes: z.array(MarketingBlockChangeSchema),
  publishedVersionId: z.string().nullable(),
  publishedVersionNumber: z.number().int().nullable(),
});
export type MarketingDiffSummary = z.infer<typeof MarketingDiffSummarySchema>;

/**
 * Pure helper that compares two block trees by id. Used by the server
 * (`getDiffSummary`) and the client (`VersionDiffSheet`) so both share
 * exactly one definition of "what counts as a change".
 *
 * Identity rules:
 *   - A block id present in both lists with deep-equal content → `unchanged`.
 *   - A block id present in both lists with differing content → `modified`,
 *     plus a `changedProps` list naming the top-level prop keys that differ.
 *   - A block id only in `next` → `added`.
 *   - A block id only in `prev` → `removed`.
 *
 * The `changes` array preserves draft order for blocks present in `next`,
 * then appends removed blocks (in their original `prev` order) so the
 * diff renders top-to-bottom in a stable way.
 */
export function diffBlocks(
  prev: Block[],
  next: Block[],
): Pick<MarketingDiffSummary, 'added' | 'removed' | 'modified' | 'unchanged' | 'changes'> {
  const prevById = new Map(prev.map((b) => [b.id, b]));
  const nextById = new Map(next.map((b) => [b.id, b]));

  let added = 0;
  let removed = 0;
  let modified = 0;
  let unchanged = 0;
  const changes: MarketingBlockChange[] = [];

  for (const block of next) {
    const before = prevById.get(block.id);
    if (!before) {
      added++;
      changes.push({
        kind: 'added',
        blockId: block.id,
        blockType: block.type,
        title: blockDisplayTitle(block),
        changedProps: [],
      });
      continue;
    }
    if (before.type !== block.type) {
      modified++;
      changes.push({
        kind: 'modified',
        blockId: block.id,
        blockType: block.type,
        title: blockDisplayTitle(block),
        changedProps: ['type'],
      });
      continue;
    }
    const changedProps = diffProps(
      (before.props ?? {}) as Record<string, unknown>,
      (block.props ?? {}) as Record<string, unknown>,
    );
    if (changedProps.length === 0) {
      unchanged++;
      changes.push({
        kind: 'unchanged',
        blockId: block.id,
        blockType: block.type,
        title: blockDisplayTitle(block),
        changedProps: [],
      });
    } else {
      modified++;
      changes.push({
        kind: 'modified',
        blockId: block.id,
        blockType: block.type,
        title: blockDisplayTitle(block),
        changedProps,
      });
    }
  }

  for (const block of prev) {
    if (nextById.has(block.id)) continue;
    removed++;
    changes.push({
      kind: 'removed',
      blockId: block.id,
      blockType: block.type,
      title: blockDisplayTitle(block),
      changedProps: [],
    });
  }

  return { added, removed, modified, unchanged, changes };
}

/**
 * Best-effort human label for a block. The marketing inspector typically
 * shows "Block · Hero" headings; we want the diff list to be more
 * specific (e.g. "Hero · 'Workflows that just work'") so users can scan
 * which copy block they actually edited. Falls back to `null` (rendered
 * as "Untitled <type>" by the UI) for blocks with no obvious label.
 */
function blockDisplayTitle(block: Block): string | null {
  const props = block.props as Record<string, unknown>;
  const candidates = ['title', 'eyebrow', 'caption', 'replayId'];
  for (const key of candidates) {
    const v = props[key];
    if (typeof v === 'string' && v.trim().length > 0) return v.trim();
  }
  return null;
}

/** Returns the top-level prop keys whose JSON-serialized values differ. */
function diffProps(before: Record<string, unknown>, after: Record<string, unknown>): string[] {
  const keys = new Set<string>([...Object.keys(before), ...Object.keys(after)]);
  const changed: string[] = [];
  for (const key of keys) {
    if (JSON.stringify(before[key]) !== JSON.stringify(after[key])) {
      changed.push(key);
    }
  }
  return changed.sort();
}

/** Shape of a published page served to unauthed visitors. */
export const PublishedPageSchema = z.object({
  slug: z.string(),
  title: z.string(),
  description: z.string(),
  icon: MarketingIconNameSchema,
  order: z.number().int(),
  blocks: BlocksSchema,
  publishedAt: z.number(),
  /**
   * Mirror of `PageMetaSchema.ogImage`. Surfaced on the public payload
   * so SSR can emit `og:image` without doing another lookup, and so the
   * client-side hydration script tag carries the same data the server
   * rendered.
   */
  ogImage: MarketingImageRefSchema.optional(),
});
export type PublishedPage = z.infer<typeof PublishedPageSchema>;

/** Summary entry in the public index listing. */
export const PublishedPageSummarySchema = PublishedPageSchema.omit({ blocks: true });
export type PublishedPageSummary = z.infer<typeof PublishedPageSummarySchema>;

// --- Create / update payloads ---

export const CreateMarketingPageSchema = z.object({
  slug: PageMetaSchema.shape.slug,
  title: z.string().min(1),
  description: z.string().optional(),
  icon: MarketingIconNameSchema.optional(),
  order: z.number().int().optional(),
  draftBlocks: BlocksSchema.optional(),
  ogImage: MarketingImageRefSchema.optional(),
});
export type CreateMarketingPagePayload = z.infer<typeof CreateMarketingPageSchema>;

/**
 * `null` is permitted for `ogImage` so callers can explicitly clear the
 * stored image (the editor surfaces a "Remove" affordance). `undefined`
 * means "no change", mirroring every other field in this payload.
 */
export const UpdateMarketingPageSchema = z.object({
  slug: PageMetaSchema.shape.slug.optional(),
  title: z.string().min(1).optional(),
  description: z.string().optional(),
  icon: MarketingIconNameSchema.optional(),
  order: z.number().int().optional(),
  draftBlocks: BlocksSchema.optional(),
  ogImage: MarketingImageRefSchema.nullable().optional(),
});
export type UpdateMarketingPagePayload = z.infer<typeof UpdateMarketingPageSchema>;
